import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Button } from 'reactstrap';
import { AuthProvider } from 'react-oidc-context';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { oidcConfig } from './auth/oidcConfig';
import { MeProvider } from './auth/MeContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthGuard } from './auth/AuthGuard';
import { RoleGuard } from './auth/RoleGuard';
import { queryClient } from './api/queryClient';
import LoginCallback from './pages/LoginCallback';
import NotAuthorized from './pages/NotAuthorized';
import Home from './pages/Home';
import DashboardLayout from './layouts/DashboardLayout';
import ItemSchemasList from './pages/admin/ItemSchemasList';
import ItemSchemaEditor from './pages/admin/ItemSchemaEditor';
import DialogTemplatesList from './pages/admin/DialogTemplatesList';
import DialogTemplateEditor from './pages/admin/DialogTemplateEditor';
import TenantsList from './pages/admin/TenantsList';
import TenantsNew from './pages/admin/TenantsNew';
import TenantDetail from './pages/admin/TenantDetail';
import AuditCrossTenant from './pages/admin/AuditCrossTenant';
import AuditTenant from './pages/admin/AuditTenant';
import PickListsList from './pages/admin/PickListsList';
import WarehousesPage from './pages/admin/WarehousesPage';
import UsersPage from './pages/admin/UsersPage';
import AssignmentsBoardPage from './pages/admin/AssignmentsBoard';
import VoiceConfigPage from './pages/admin/VoiceConfigPage';
import LiveMonitoringPage from './pages/admin/LiveMonitoringPage';

export default function App() {
  return (
    <ThemeProvider>
    <AuthProvider {...oidcConfig}>
      <QueryClientProvider client={queryClient}>
        <MeProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login/callback" element={<LoginCallback />} />
              <Route path="/not-authorized" element={<NotAuthorized />} />
              <Route
                path="/"
                element={
                  <AuthGuard>
                    <DashboardLayout viewTitle="Dashboard">
                      <Home />
                    </DashboardLayout>
                  </AuthGuard>
                }
              />
              <Route path="/admin" element={<Navigate to="/admin/item-schemas" replace />} />
              <Route path="/admin/item-schemas" element={
                <AuthGuard><RoleGuard allow={['platform-admin']}>
                  <DashboardLayout viewTitle="Item schemas"><ItemSchemasList /></DashboardLayout>
                </RoleGuard></AuthGuard>
              } />
              <Route path="/admin/item-schemas/new" element={
                <AuthGuard><RoleGuard allow={['platform-admin']}>
                  <DashboardLayout viewTitle="Item schemas — nuova versione"><ItemSchemaEditor /></DashboardLayout>
                </RoleGuard></AuthGuard>
              } />
              <Route path="/admin/item-schemas/:id" element={
                <AuthGuard><RoleGuard allow={['platform-admin']}>
                  <DashboardLayout viewTitle="Item schemas — dettaglio"><ItemSchemaEditor /></DashboardLayout>
                </RoleGuard></AuthGuard>
              } />
              <Route path="/admin/dialog-templates" element={
                <AuthGuard><RoleGuard allow={['platform-admin']}>
                  <DashboardLayout viewTitle="Dialog templates"><DialogTemplatesList /></DashboardLayout>
                </RoleGuard></AuthGuard>
              } />
              <Route path="/admin/dialog-templates/new" element={
                <AuthGuard><RoleGuard allow={['platform-admin']}>
                  <DashboardLayout viewTitle="Dialog template — nuova versione"><DialogTemplateEditor /></DashboardLayout>
                </RoleGuard></AuthGuard>
              } />
              <Route path="/admin/dialog-templates/:id" element={
                <AuthGuard><RoleGuard allow={['platform-admin']}>
                  <DashboardLayout viewTitle="Dialog template — dettaglio"><DialogTemplateEditor /></DashboardLayout>
                </RoleGuard></AuthGuard>
              } />
              <Route path="/admin/tenants" element={
                <AuthGuard><RoleGuard allow={['platform-admin']}>
                  <DashboardLayout viewTitle="Tenants"><TenantsList /></DashboardLayout>
                </RoleGuard></AuthGuard>
              } />
              <Route path="/admin/tenants/new" element={
                <AuthGuard><RoleGuard allow={['platform-admin']}>
                  <DashboardLayout viewTitle="Tenants — nuovo"><TenantsNew /></DashboardLayout>
                </RoleGuard></AuthGuard>
              } />
              <Route path="/admin/tenants/:id" element={
                <AuthGuard><RoleGuard allow={['platform-admin']}>
                  <DashboardLayout viewTitle="Tenant"><TenantDetail /></DashboardLayout>
                </RoleGuard></AuthGuard>
              } />
              <Route path="/admin/audit" element={
                <AuthGuard><RoleGuard allow={['platform-admin']}>
                  <DashboardLayout viewTitle="Audit cross-tenant"><AuditCrossTenant /></DashboardLayout>
                </RoleGuard></AuthGuard>
              } />
              <Route path="/warehouses" element={
                <AuthGuard><RoleGuard allow={['tenant-admin', 'voice-supervisor']}>
                  <DashboardLayout viewTitle="Magazzini"><WarehousesPage /></DashboardLayout>
                </RoleGuard></AuthGuard>
              } />
              <Route path="/users" element={
                <AuthGuard><RoleGuard allow={['tenant-admin', 'voice-supervisor']}>
                  <DashboardLayout viewTitle="Utenti"><UsersPage /></DashboardLayout>
                </RoleGuard></AuthGuard>
              } />
              <Route path="/audit" element={
                <AuthGuard><RoleGuard allow={['tenant-admin', 'warehouse-admin', 'voice-supervisor']}>
                  <DashboardLayout viewTitle="Audit"><AuditTenant /></DashboardLayout>
                </RoleGuard></AuthGuard>
              } />
              <Route path="/pick-lists" element={
                <AuthGuard><RoleGuard allow={['tenant-admin', 'warehouse-admin', 'voice-supervisor']}>
                  <DashboardLayout viewTitle="Pick-list"><PickListsList /></DashboardLayout>
                </RoleGuard></AuthGuard>
              } />
              {/* SP-5l: detail is inline in the list; deep-links pre-expand the accordion. */}
              <Route path="/pick-lists/:id" element={
                <AuthGuard><RoleGuard allow={['tenant-admin', 'warehouse-admin', 'voice-supervisor']}>
                  <DashboardLayout viewTitle="Pick-list"><PickListsList /></DashboardLayout>
                </RoleGuard></AuthGuard>
              } />
              <Route path="/operations/assignments" element={
                <AuthGuard><RoleGuard allow={['warehouse-admin']}>
                  <DashboardLayout viewTitle="Assegnazioni"><AssignmentsBoardPage /></DashboardLayout>
                </RoleGuard></AuthGuard>
              } />
              <Route path="/voice-config" element={
                <AuthGuard><RoleGuard allow={['platform-admin']}>
                  <DashboardLayout viewTitle="Voice provider"><VoiceConfigPage /></DashboardLayout>
                </RoleGuard></AuthGuard>
              } />
              <Route path="/live" element={
                <AuthGuard><RoleGuard allow={['tenant-admin', 'warehouse-admin', 'voice-supervisor']}>
                  <DashboardLayout viewTitle="Live monitoring"><LiveMonitoringPage /></DashboardLayout>
                </RoleGuard></AuthGuard>
              } />
              <Route path="*" element={
                <div className="vp-root" style={{
                  minHeight: '100vh', background: 'var(--vp-bg)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
                }}>
                  <div style={{
                    maxWidth: 440, textAlign: 'center', padding: 32,
                    background: 'var(--vp-surface)', border: '1px solid var(--vp-border)',
                    borderRadius: 'var(--vp-r-lg)', boxShadow: 'var(--vp-shadow-md)',
                  }}>
                    <div style={{
                      width: 56, height: 56, borderRadius: '50%',
                      background: 'var(--vp-warn-soft)', color: 'var(--vp-warn)',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 24, marginBottom: 16,
                    }}>
                      <i className="bi bi-question-circle" />
                    </div>
                    <h1 style={{ fontSize: 'var(--vp-text-xl)', margin: '0 0 6px', color: 'var(--vp-text)' }}>
                      Pagina non trovata
                    </h1>
                    <p style={{ color: 'var(--vp-text-3)', fontSize: 'var(--vp-text-sm)', margin: '0 0 20px' }}>
                      L'URL richiesto non corrisponde a nessuna pagina della dashboard.
                    </p>
                    <Button color="primary" size="sm" tag="a" href="/" style={{ textDecoration: 'none' }}>
                      Torna alla home
                    </Button>
                  </div>
                </div>
              } />
            </Routes>
          </BrowserRouter>
        </MeProvider>
        {import.meta.env.DEV && (
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
        )}
      </QueryClientProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}
